import { c as _c } from "react/compiler-runtime";
/**
 * `useDraggablePill` — the drag state machine for the DevPill, extracted
 * into a single hook seam so `dev-pill.tsx` doesn't have to host the entire
 * drag lifecycle inline.
 *
 * Encapsulation contract (per `.claude/rules/encapsulation.md` principles
 * #1 data-in, #2 interface-based boundaries, #6 fix at the source):
 *
 *   - All element inputs (`pillEl`, `fabEl`, `containerEl`) and consumer
 *     state (`position`, `nextjsVisible`, `offset`) flow in as parameters;
 *     the hook reads viewport dimensions from `window` for positioning but
 *     does not query DOM nodes globally.
 *   - Consumers see only the typed `UseDraggablePillResult` surface —
 *     `edgePosition` (the snapped state) and the `data-*` attributes to
 *     spread onto `.pill-container`. Internal refs (`isDraggingRef`,
 *     `suppressNextClickRef`) never leak.
 *   - All teardown (pointer listeners, ResizeObserver, store subscribe)
 *     stays inside the hook's `useEffect` cleanups.
 *
 * A sibling draggable hook `useDraggableResizable` exists in
 * `lib/dev-ui/src/bug-report/use-draggable-resizable.ts` for the bug-report
 * panel. The two solve different problems (free-positioned mouse-only
 * panel with resize vs. four-edge pointer-snap with persistence + click
 * suppression) and share no useful primitive.
 */

import { useSyncRef } from '@fpo3/ts-commons-ui';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { PillPosition } from './adapters';
import { computePillHostStyle, type EdgePosition, edgePositionFromPillPosition, FALLBACK_PILL_HEIGHT, FALLBACK_PILL_WIDTH, FALLBACK_VIEWPORT_HEIGHT, FALLBACK_VIEWPORT_WIDTH, FINE_POINTER_PERPENDICULAR_MARGIN, PERPENDICULAR_MARGIN, type PillOffset, pillPositionStore, popupDirectionFor, snapToNearestEdge } from './pill-positioning';

/** Movement threshold in px before pointerdown turns into a drag (prevents accidental drags swallowing button clicks). */
const DRAG_THRESHOLD_PX = 5;
/** Debounce window (ms) for the ResizeObserver callback so the mobile FAB collapse/expand transition does not fire a setMeasuredSize per animation frame. */
const RESIZE_DEBOUNCE_MS = 150;
/** Debounce window (ms) for the window resize listener. Distinct from {@link RESIZE_DEBOUNCE_MS} — element-resize and viewport-resize are different signals; coupling them to one knob would tie unrelated tuning. */
const WINDOW_RESIZE_DEBOUNCE_MS = 150;
/** CSS transition applied to the host so the post-drag/resize "settle" (re-clamp clear of app chrome) animates. Disabled during live drag so dragging tracks the finger instantly. */
const SETTLE_TRANSITION = 'top 0.18s ease, left 0.18s ease, right 0.18s ease, bottom 0.18s ease';
export interface UseDraggablePillOptions {
  /** The visible `.pill` box on desktop. Drag listeners attach to this when present. */
  pillEl: HTMLElement | null;
  /** The collapsed `.pill-fab` button on mobile. Drag listeners attach to this when present. */
  fabEl: HTMLElement | null;
  /** The `.pill-container` wrapper. Used to find the shadow-DOM host element for style writes. */
  containerEl: HTMLElement | null;
  /** Legacy `position` prop / detected position — used to seed the initial edge when nothing is in localStorage.
   *  The `'hidden'` value is handled at the component boundary (see
   *  `DevPillImpl`); the hook never receives it. */
  position: Exclude<PillPosition, 'hidden'>;
  /** Whether the Next.js dev indicator is visible — when true, the pill clears the indicator with a 64px nudge near the right corner. */
  nextjsVisible: boolean;
  /** Consumer-supplied per-side offset (e.g. clear a mobile chat input bar). */
  offset?: PillOffset | undefined;
}
export interface UseDraggablePillResult {
  /** The currently-snapped edge position. Always set — `'hidden'` is
   *  handled at the component boundary, so the hook's return type never
   *  carries the sentinel. */
  edgePosition: EdgePosition;
  /** Attributes to spread onto the `.pill-container` so CSS attribute selectors pick up the docked edge + popup direction. */
  containerAttrs: {
    'data-edge': 'left' | 'right' | 'top' | 'bottom';
    'data-popup-down': 'true' | undefined;
    'data-popup-right-anchor': 'true' | undefined;
  };
}

/**
 * Pick the first candidate element whose `getBoundingClientRect()` has
 * both positive width AND positive height. This is the correct way to
 * measure the visible inner box when the same `pill-container` carries
 * both a desktop `.pill` and a mobile `.pill-fab` — under the mobile
 * media query the desktop pill is `display: none` (zero rect) and the
 * FAB is the real measurement source, and vice versa. Passing the
 * candidates in priority order — desktop first, FAB second — means the
 * desktop measurement wins when both are real.
 *
 * Fallback constants only kick in when nothing measurable exists (early
 * mount, both elements unmounted) — never when at least one of them has
 * been laid out.
 */
function firstNonZeroRect(els: ReadonlyArray<HTMLElement | null>): DOMRect | null {
  for (const el of els) {
    const rect = el?.getBoundingClientRect();
    if (rect && rect.width > 0 && rect.height > 0) {
      return rect;
    }
  }
  return null;
}

/** Returns the width and height of the first laid-out element in `els`, falling back to pill fallback constants when all rects are zero. */
function measureFirstNonZero(els: ReadonlyArray<HTMLElement | null>): {
  width: number;
  height: number;
} {
  const rect = firstNonZeroRect(els);
  return rect ? {
    width: rect.width,
    height: rect.height
  } : {
    width: FALLBACK_PILL_WIDTH,
    height: FALLBACK_PILL_HEIGHT
  };
}

/** Read the current viewport size from `window`. Pulled out into a helper so
 *  the resize-listener effect and the seed-from-prop effect both go through
 *  one sanctioned source. */
function readViewportSize(): {
  width: number;
  height: number;
} {
  return {
    width: window.innerWidth,
    height: window.innerHeight
  };
}

/** Media query that distinguishes touch (coarse) from mouse (fine) pointers. */
const COARSE_POINTER_QUERY = '(pointer: coarse)';

/**
 * Whether the primary pointer is coarse (touch). Defaults to `true` (the
 * protective {@link PERPENDICULAR_MARGIN}) when `matchMedia` is unavailable
 * (SSR, jsdom without a stub) — the conservative choice that preserves the
 * pre-existing always-on margin where we can't detect the pointer.
 */
function readCoarsePointer(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  return window.matchMedia(COARSE_POINTER_QUERY).matches;
}

/** Manages drag state, edge snapping, cross-tab sync, and style writes for the DevPill, exposing only the snapped edge position and container attributes needed to render. */
export function useDraggablePill(opts) {
  const $ = _c(59);
  const {
    pillEl,
    fabEl,
    containerEl,
    position,
    nextjsVisible,
    offset
  } = opts;
  const offsetRef = useSyncRef(offset);
  const isDraggingRef = useRef(false);
  const settleReadyRef = useRef(false);
  const suppressNextClickRef = useRef(false);
  const claimedRef = useRef(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (pillPositionStore.read() != null) {
        claimedRef.current = true;
      }
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  const [coarsePointer, setCoarsePointer] = useState(_temp);
  const perpendicularMargin = coarsePointer ? PERPENDICULAR_MARGIN : FINE_POINTER_PERPENDICULAR_MARGIN;
  const perpendicularMarginRef = useSyncRef(perpendicularMargin);
  let t2;
  if ($[2] !== position) {
    t2 = () => pillPositionStore.read() ?? edgePositionFromPillPosition(position, {
      width: FALLBACK_VIEWPORT_WIDTH,
      height: FALLBACK_VIEWPORT_HEIGHT
    }, {
      width: FALLBACK_PILL_WIDTH,
      height: FALLBACK_PILL_HEIGHT
    }, readCoarsePointer() ? PERPENDICULAR_MARGIN : FINE_POINTER_PERPENDICULAR_MARGIN);
    $[2] = position;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [edgePosition, setEdgePosition] = useState(t2);
  const [viewportSize, setViewportSize] = useState(_temp2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      width: FALLBACK_PILL_WIDTH,
      height: FALLBACK_PILL_HEIGHT
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const [measuredSize, setMeasuredSize] = useState(t3);
  let t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const initial = pillPositionStore.read();
      if (initial != null) {
        setEdgePosition(initial);
        claimedRef.current = true;
      }
      return pillPositionStore.subscribe(() => {
        const next = pillPositionStore.read();
        if (next != null) {
          setEdgePosition(next);
          claimedRef.current = true;
        }
      });
    };
    t5 = [];
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  if ($[7] !== edgePosition.edge || $[8] !== edgePosition.perpendicularOffset || $[9] !== fabEl || $[10] !== perpendicularMargin || $[11] !== pillEl || $[12] !== position || $[13] !== viewportSize) {
    t6 = () => {
      if (claimedRef.current) {
        return;
      }
      if (pillPositionStore.read() != null) {
        claimedRef.current = true;
        return;
      }
      const measured = measureFirstNonZero([pillEl, fabEl]);
      const next_0 = edgePositionFromPillPosition(position, viewportSize, measured, perpendicularMargin);
      if (edgePosition.edge === next_0.edge && edgePosition.perpendicularOffset === next_0.perpendicularOffset) {
        return;
      }
      setEdgePosition(next_0);
    };
    $[7] = edgePosition.edge;
    $[8] = edgePosition.perpendicularOffset;
    $[9] = fabEl;
    $[10] = perpendicularMargin;
    $[11] = pillEl;
    $[12] = position;
    $[13] = viewportSize;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== containerEl || $[16] !== edgePosition || $[17] !== fabEl || $[18] !== perpendicularMargin || $[19] !== pillEl || $[20] !== position) {
    t7 = [position, containerEl, pillEl, fabEl, edgePosition, perpendicularMargin];
    $[15] = containerEl;
    $[16] = edgePosition;
    $[17] = fabEl;
    $[18] = perpendicularMargin;
    $[19] = pillEl;
    $[20] = position;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[22] !== containerEl || $[23] !== edgePosition || $[24] !== measuredSize || $[25] !== nextjsVisible || $[26] !== offsetRef || $[27] !== perpendicularMargin || $[28] !== viewportSize) {
    t8 = () => {
      if (!containerEl) {
        return;
      }
      if (isDraggingRef.current) {
        return;
      }
      const host = (containerEl.getRootNode() as ShadowRoot).host as HTMLElement;
      if (!host) {
        return;
      }
      host.style.transition = settleReadyRef.current ? SETTLE_TRANSITION : "none";
      settleReadyRef.current = true;
      Object.assign(host.style, computePillHostStyle(edgePosition, nextjsVisible, viewportSize, measuredSize, offsetRef.current, perpendicularMargin));
    };
    t9 = [containerEl, edgePosition, viewportSize, measuredSize, nextjsVisible, offsetRef, perpendicularMargin];
    $[22] = containerEl;
    $[23] = edgePosition;
    $[24] = measuredSize;
    $[25] = nextjsVisible;
    $[26] = offsetRef;
    $[27] = perpendicularMargin;
    $[28] = viewportSize;
    $[29] = t8;
    $[30] = t9;
  } else {
    t8 = $[29];
    t9 = $[30];
  }
  useLayoutEffect(t8, t9);
  let t10;
  let t11;
  if ($[31] !== fabEl || $[32] !== pillEl) {
    t10 = () => {
      if (pillEl == null && fabEl == null) {
        return;
      }
      const next_1 = measureFirstNonZero([pillEl, fabEl]);
      setMeasuredSize(prev => prev.width === next_1.width && prev.height === next_1.height ? prev : next_1);
    };
    t11 = [pillEl, fabEl];
    $[31] = fabEl;
    $[32] = pillEl;
    $[33] = t10;
    $[34] = t11;
  } else {
    t10 = $[33];
    t11 = $[34];
  }
  useLayoutEffect(t10, t11);
  let t12;
  let t13;
  if ($[35] !== fabEl || $[36] !== pillEl) {
    t12 = () => {
      if (pillEl == null && fabEl == null) {
        return;
      }
      let debounceTimer = null;
      const observer = new ResizeObserver(() => {
        if (debounceTimer != null) {
          clearTimeout(debounceTimer);
        }
        debounceTimer = setTimeout(() => {
          debounceTimer = null;
          if (isDraggingRef.current) {
            return;
          }
          const next_2 = measureFirstNonZero([pillEl, fabEl]);
          setMeasuredSize(prev_0 => prev_0.width === next_2.width && prev_0.height === next_2.height ? prev_0 : next_2);
        }, RESIZE_DEBOUNCE_MS);
      });
      if (pillEl) {
        observer.observe(pillEl);
      }
      if (fabEl) {
        observer.observe(fabEl);
      }
      return () => {
        if (debounceTimer != null) {
          clearTimeout(debounceTimer);
        }
        observer.disconnect();
      };
    };
    t13 = [pillEl, fabEl];
    $[35] = fabEl;
    $[36] = pillEl;
    $[37] = t12;
    $[38] = t13;
  } else {
    t12 = $[37];
    t13 = $[38];
  }
  useEffect(t12, t13);
  let t14;
  let t15;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      if (typeof window === "undefined") {
        return;
      }
      let debounceTimer_0 = null;
      const onResize = function onResize() {
        if (debounceTimer_0 != null) {
          clearTimeout(debounceTimer_0);
        }
        debounceTimer_0 = setTimeout(() => {
          debounceTimer_0 = null;
          const next_3 = readViewportSize();
          setViewportSize(prev_1 => prev_1.width === next_3.width && prev_1.height === next_3.height ? prev_1 : next_3);
        }, WINDOW_RESIZE_DEBOUNCE_MS);
      };
      window.addEventListener("resize", onResize);
      return () => {
        if (debounceTimer_0 != null) {
          clearTimeout(debounceTimer_0);
        }
        window.removeEventListener("resize", onResize);
      };
    };
    t15 = [];
    $[39] = t14;
    $[40] = t15;
  } else {
    t14 = $[39];
    t15 = $[40];
  }
  useEffect(t14, t15);
  let t16;
  let t17;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => {
      if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
        return;
      }
      const mql = window.matchMedia(COARSE_POINTER_QUERY);
      const onChange = function onChange() {
        setCoarsePointer(mql.matches);
      };
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    };
    t17 = [];
    $[41] = t16;
    $[42] = t17;
  } else {
    t16 = $[41];
    t17 = $[42];
  }
  useEffect(t16, t17);
  let t18;
  if ($[43] !== containerEl || $[44] !== fabEl || $[45] !== perpendicularMarginRef || $[46] !== pillEl) {
    t18 = () => {
      if (!containerEl) {
        return;
      }
      if (pillEl == null && fabEl == null) {
        return;
      }
      const host_0 = (containerEl.getRootNode() as ShadowRoot).host as HTMLElement;
      if (!host_0) {
        return;
      }
      let dragStart = null;
      let suppressClickTimer = null;
      let windowListenersInstalled = false;
      const detachWindowListeners = function detachWindowListeners() {
        if (!windowListenersInstalled) {
          return;
        }
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", endDrag);
        window.removeEventListener("pointercancel", endDrag);
        windowListenersInstalled = false;
      };
      const onPointerDown = function onPointerDown(e) {
        if (e.button !== 0) {
          return;
        }
        isDraggingRef.current = false;
        const hostRect = host_0.getBoundingClientRect();
        const pillRect = firstNonZeroRect([pillEl, fabEl]) ?? hostRect;
        dragStart = {
          pointerId: e.pointerId,
          startX: e.clientX,
          startY: e.clientY,
          grabOffsetX: e.clientX - pillRect.left,
          grabOffsetY: e.clientY - pillRect.top,
          hostToPillDX: pillRect.left - hostRect.left,
          hostToPillDY: pillRect.top - hostRect.top
        };
        detachWindowListeners();
        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", endDrag);
        window.addEventListener("pointercancel", endDrag);
        windowListenersInstalled = true;
      };
      function onPointerMove(e_0) {
        if (dragStart == null || e_0.pointerId !== dragStart.pointerId) {
          return;
        }
        const dx = e_0.clientX - dragStart.startX;
        const dy = e_0.clientY - dragStart.startY;
        if (!isDraggingRef.current) {
          if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) {
            return;
          }
          isDraggingRef.current = true;
        }
        if (e_0.pointerType === "touch" || e_0.pointerType === "pen") {
          e_0.preventDefault();
        }
        const pillLeft = e_0.clientX - dragStart.grabOffsetX;
        const pillTop = e_0.clientY - dragStart.grabOffsetY;
        host_0.style.transition = "none";
        host_0.style.left = `${pillLeft - dragStart.hostToPillDX}px`;
        host_0.style.top = `${pillTop - dragStart.hostToPillDY}px`;
        host_0.style.right = "";
        host_0.style.bottom = "";
      }
      function endDrag(e_1) {
        if (dragStart == null || e_1.pointerId !== dragStart.pointerId) {
          return;
        }
        detachWindowListeners();
        const wasDragging = isDraggingRef.current;
        dragStart = null;
        if (!wasDragging) {
          return;
        }
        isDraggingRef.current = false;
        const pillRect_0 = firstNonZeroRect([pillEl, fabEl]) ?? host_0.getBoundingClientRect();
        const viewport = readViewportSize();
        const snapped = snapToNearestEdge({
          x: pillRect_0.left,
          y: pillRect_0.top,
          width: pillRect_0.width,
          height: pillRect_0.height
        }, viewport, perpendicularMarginRef.current);
        pillPositionStore.set(snapped);
        claimedRef.current = true;
        suppressNextClickRef.current = true;
        if (suppressClickTimer != null) {
          clearTimeout(suppressClickTimer);
        }
        suppressClickTimer = setTimeout(() => {
          suppressNextClickRef.current = false;
          suppressClickTimer = null;
        }, 0);
      }
      const onClickCapture = function onClickCapture(e_2) {
        if (suppressNextClickRef.current) {
          suppressNextClickRef.current = false;
          e_2.stopPropagation();
          e_2.preventDefault();
        }
      };
      const targets = [];
      if (pillEl) {
        targets.push(pillEl);
      }
      if (fabEl) {
        targets.push(fabEl);
      }
      for (const target of targets) {
        target.addEventListener("pointerdown", onPointerDown);
        target.addEventListener("click", onClickCapture, {
          capture: true
        });
      }
      return () => {
        if (suppressClickTimer != null) {
          clearTimeout(suppressClickTimer);
          suppressClickTimer = null;
        }
        detachWindowListeners();
        for (const target_0 of targets) {
          target_0.removeEventListener("pointerdown", onPointerDown);
          target_0.removeEventListener("click", onClickCapture, {
            capture: true
          });
        }
      };
    };
    $[43] = containerEl;
    $[44] = fabEl;
    $[45] = perpendicularMarginRef;
    $[46] = pillEl;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== containerEl || $[49] !== fabEl || $[50] !== pillEl) {
    t19 = [containerEl, pillEl, fabEl];
    $[48] = containerEl;
    $[49] = fabEl;
    $[50] = pillEl;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  useEffect(t18, t19);
  const topClearance = offset?.topClearance;
  const bottomClearance = offset?.bottomClearance;
  const leftClearance = offset?.leftClearance;
  const rightClearance = offset?.rightClearance;
  const popupDir = popupDirectionFor(edgePosition, viewportSize, measuredSize, {
    topClearance,
    bottomClearance,
    leftClearance,
    rightClearance
  }, perpendicularMargin);
  const t20 = popupDir.down ? "true" as const : undefined;
  const t21 = popupDir.rightAnchor ? "true" as const : undefined;
  let t22;
  if ($[52] !== edgePosition.edge || $[53] !== t20 || $[54] !== t21) {
    t22 = {
      "data-edge": edgePosition.edge,
      "data-popup-down": t20,
      "data-popup-right-anchor": t21
    };
    $[52] = edgePosition.edge;
    $[53] = t20;
    $[54] = t21;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  const containerAttrs = t22;
  let t23;
  if ($[56] !== containerAttrs || $[57] !== edgePosition) {
    t23 = {
      edgePosition,
      containerAttrs
    };
    $[56] = containerAttrs;
    $[57] = edgePosition;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  return t23;
}
function _temp2() {
  return typeof window === "undefined" ? {
    width: FALLBACK_VIEWPORT_WIDTH,
    height: FALLBACK_VIEWPORT_HEIGHT
  } : readViewportSize();
}
function _temp() {
  return readCoarsePointer();
}